import React, { useState, useMemo } from "react";
import SenderScreen from "./SenderScreen";
import ReceiverScreen from "./ReceiverScreen";
import { useTranslationSocket } from "./useTranslationSocket";
import { CustomIcon } from "../../shared/CustomIcon";

// ============================================================
// LiveTab — top-level Live screen for the Translation dept.
// Decides which sub-view to show based on the user's current
// session role (sender | receiver | observer).
// ============================================================

export default function LiveTab({ deptId }) {
  const socket = useTranslationSocket(deptId, { enabled: Boolean(deptId) });
  const [modeOverride, setModeOverride] = useState(null); // null | "sender" | "receiver" | "observer"

  const effectiveRole = modeOverride || socket.me?.yourRole || "observer";
  const canBeSender = useMemo(() => {
    if (!socket.me) return false;
    return socket.me.isLeader || socket.me.isSuperadmin || socket.promotedSenders.includes(socket.me.yourUserId);
  }, [socket.me, socket.promotedSenders]);

  // Role switcher — opens a tiny picker modal.
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const applyRole = (role) => {
    setModeOverride(role);
    socket.setRole(role);
    setSwitcherOpen(false);
  };

  if (!socket.me) {
    return (
      <div
        style={{ padding: 40, textAlign: "center", opacity: 0.6 }}
        data-testid="translation-live-connecting"
      >
        {socket.connected ? "Joining session…" : "Connecting to live session…"}
      </div>
    );
  }

  return (
    <div data-testid="translation-live-tab" style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 480, gap: 12 }}>
      {effectiveRole === "sender" ? (
        <SenderScreen
          connected={socket.connected}
          phrases={socket.phrases}
          me={socket.me}
          roster={socket.roster}
          sendPhrase={socket.sendPhrase}
          editPhrase={socket.editPhrase}
          clearScreen={socket.clearScreen}
          promote={socket.promote}
          demote={socket.demote}
          promotedSenders={socket.promotedSenders}
          onSwitchRole={() => setSwitcherOpen(true)}
        />
      ) : effectiveRole === "receiver" ? (
        <ReceiverScreen
          connected={socket.connected}
          phrases={socket.phrases}
          cleared={socket.cleared}
          onSwitchRole={() => setSwitcherOpen(true)}
        />
      ) : (
        <ObserverScreen
          connected={socket.connected}
          phrases={socket.phrases}
          roster={socket.roster}
          onSwitchRole={() => setSwitcherOpen(true)}
          canBeSender={canBeSender}
        />
      )}

      {/* Role switcher modal */}
      {switcherOpen && (
        <div
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setSwitcherOpen(false)}
          data-testid="translation-role-switcher"
        >
          <div className="modal" style={{ maxWidth: 420, width: "min(96vw, 420px)" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
                <CustomIcon name="people" size={16} /> Switch role
              </h3>
              <button className="modal-close" onClick={() => setSwitcherOpen(false)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
              <RoleButton active={effectiveRole === "sender"} onClick={() => applyRole("sender")} disabled={!canBeSender} testid="translation-role-choose-sender">
                Sender <small style={{ opacity: 0.65 }}>— type and push phrases{!canBeSender ? " (not permitted)" : ""}</small>
              </RoleButton>
              <RoleButton active={effectiveRole === "receiver"} onClick={() => applyRole("receiver")} testid="translation-role-choose-receiver">
                Receiver <small style={{ opacity: 0.65 }}>— public-facing display with wake-lock</small>
              </RoleButton>
              <RoleButton active={effectiveRole === "observer"} onClick={() => applyRole("observer")} testid="translation-role-choose-observer">
                Observer <small style={{ opacity: 0.65 }}>— read-only session view</small>
              </RoleButton>
            </div>
          </div>
        </div>
      )}

      {socket.lastError && (
        <div
          data-testid="translation-live-last-error"
          style={{ position: "fixed", bottom: 16, right: 16, padding: "8px 14px", background: "rgba(200,40,40,0.9)", color: "#fff", borderRadius: 6, fontSize: 12 }}
        >
          {socket.lastError}
        </div>
      )}
    </div>
  );
}

function RoleButton({ active, onClick, children, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      data-testid={testid}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "12px 14px",
        borderRadius: 10,
        background: active ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-text) 6%, transparent)",
        color: active ? "#111" : "var(--kc-text)",
        border: `1px solid ${active ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 20%, transparent)"}`,
        fontWeight: 700, fontSize: 14,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        textAlign: "left",
      }}
    >{children}</button>
  );
}

function ObserverScreen({ connected, phrases, roster, onSwitchRole, canBeSender }) {
  return (
    <div data-testid="translation-observer-screen" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 14px",
        background: "color-mix(in srgb, var(--kc-accent) 8%, transparent)",
        borderRadius: 10,
      }}>
        <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: connected ? "#4ade80" : "#f87171" }} />
        <b data-testid="translation-observer-status">{connected ? "Live" : "Reconnecting…"}</b>
        <span style={{ fontSize: 12, opacity: 0.6, marginLeft: 6 }}>Observer · {roster.length} in session</span>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn btn-outline btn-sm" onClick={onSwitchRole} data-testid="translation-observer-switch-role">
          Switch Role {canBeSender ? "(you can send)" : ""}
        </button>
      </div>
      <div
        data-testid="translation-observer-list"
        style={{
          background: "#111", color: "#fff", borderRadius: 12,
          padding: 20, minHeight: 320, maxHeight: "60vh", overflowY: "auto",
          display: "flex", flexDirection: "column", gap: 12,
        }}
      >
        {phrases.length === 0 ? (
          <div style={{ margin: "auto", opacity: 0.4, fontStyle: "italic" }}>Awaiting first phrase…</div>
        ) : phrases.map(p => (
          <div key={p.id} style={{ padding: "12px 14px", borderRadius: 8, background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ fontSize: 14, opacity: 0.6 }}>{p.text}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#fff" }}>{p.translation}</div>
            <div style={{ fontSize: 10, opacity: 0.4, marginTop: 4 }}>
              {new Date(p.timestamp || Date.now()).toLocaleTimeString()} · {p.sentByName}
              {p.edited && <span style={{ color: "#D4AF37", marginLeft: 6 }}>EDITED</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
